import {TextSelection} from '@mintdata/mint-kit/api/models/Text';

export type EachParentElement = (elementRef:HTMLElement) => boolean | void;

export class DomUtils
{
    private static _canvas:HTMLCanvasElement = null;
    private static _divForTextHeight:HTMLDivElement = null;

    public static NEW_LINE_CHAR:string = '\n';

    public static getSelection(editableDiv:HTMLElement):TextSelection
    {
        let selection:TextSelection = new TextSelection(0, null);

        if( editableDiv )
        {
            const doc:Document = editableDiv.ownerDocument;
            const win:Window = doc.defaultView;

            if( typeof win.getSelection !== 'undefined' )
            {
                const sel:Selection = win.getSelection();

                if( sel.rangeCount > 0 )
                {
                    const range:Range = win.getSelection().getRangeAt(0);
                    const preCaretRange:Range = range.cloneRange();

                    preCaretRange.selectNodeContents(editableDiv);
                    preCaretRange.setEnd(range.endContainer, range.endOffset);

                    selection.startIndex = preCaretRange.toString().length;

                    const selectionString:string = range.toString();

                    if( selectionString.length > 0 )
                    {
                        selection.endIndex = selection.startIndex;
                        selection.startIndex -= selectionString.length;
                    }
                }
            }
        }

        return selection;
    }

    // TODO: Set Selection Range, not only Caret Position
    public static setSelection(editableDiv:HTMLElement, selection:TextSelection):number
    {
        if( !editableDiv || !selection )
            return -1;

        let caretPos:number = selection.startIndex;

        for( const node of editableDiv.childNodes as any )
        {
            if( node.nodeType === Node.TEXT_NODE )
            {
                if( node.length >= caretPos )
                {
                    const range:Range = document.createRange();
                    const sel:Selection = window.getSelection();

                    range.setStart(node, caretPos);
                    range.collapse(true);

                    sel.removeAllRanges();
                    sel.addRange(range);

                    return -1;
                }
                else
                    caretPos -= node.length;
            }
            else
            {
                caretPos = DomUtils.setSelection(node, new TextSelection(caretPos, selection.endIndex));

                if( caretPos === -1 )
                    return -1;
            }
        }

        return caretPos;
    }

    public static setNodeSelection(node:HTMLElement):void
    {
        if( !node )
            return;

        const range:Range = document.createRange();
        const sel:Selection = window.getSelection();

        range.setStart(node, 0);
        range.collapse(true);

        sel.removeAllRanges();
        sel.addRange(range);
    }

    public static getTextContent(element:Element):string
    {
        const textContent:string = element
            ? Array.from(element.childNodes)
                .reduce((acc:string, childNode:Node, index:number, nodes:Node[]) =>
                {
                    let contentOfChild:string = '';

                    if( childNode.nodeType === Node.TEXT_NODE )
                        contentOfChild = childNode.textContent;
                    else if( childNode.nodeType === Node.ELEMENT_NODE )
                    {
                        switch( childNode.nodeName )
                        {
                            case 'BR':
                                contentOfChild = nodes.length - 1 === index ? '' : DomUtils.NEW_LINE_CHAR;
                                break;
                            case 'DIV':
                            case 'P':
                                contentOfChild = DomUtils.NEW_LINE_CHAR + childNode.textContent;
                                break;
                            default:
                                contentOfChild = childNode.textContent;
                        }
                    }

                    return acc + contentOfChild;
                }, '')
            : '';

        return textContent
            ? textContent
                .replace(/\u00a0/g, ' ')
            : '';
    }

    public static setTextContent(element:Element, value:string):void
    {
        if( element )
        {
            value = value.replace(/&nbsp;/g, ' ');

            if( !value.trim().endsWith('<br>') )
                value += '<br>';

            element.innerHTML = value;
        }
    }

    public static isChild(child:HTMLElement, parent:HTMLElement):boolean
    {
        if( child === parent )
            return true;

        let result = false;
        DomUtils.eachParentElement(child, (element) =>
        {
            if( element === parent )
            {
                result = true;
                return false;
            }
        });
        return result;
    }

    public static selfAndEachParentElement(element:HTMLElement, handler:EachParentElement)
    {
        let currentElement = element;
        while( currentElement && currentElement.tagName !== 'BODY' )
        {
            if( handler(currentElement) === false )
                return;

            currentElement = currentElement.parentElement;
        }
    }

    public static eachParentElement(element:HTMLElement, handler:EachParentElement)
    {
        let currentElement = element;
        while( currentElement && currentElement.tagName !== 'BODY' )
        {
            currentElement = currentElement.parentElement;
            if( handler(currentElement) === false )
                return;
        }
    }

    public static resizeTextInputByContent(element:HTMLElement)
    {
        element.style.width = '0px';
        element.style.width = `${element.scrollWidth}px`;
    }

    public static getTextWidth(text:string, fontString:string, cellSettings:any = {}):number
    {
        const canvas:HTMLCanvasElement = DomUtils._canvas
            || (DomUtils._canvas = document.createElement('canvas'));
        const context:CanvasRenderingContext2D = canvas.getContext('2d');

        let font:string;

        if( fontString )
        {
            font = fontString;
        }
        else
        {
            const textItalic = cellSettings.textItalic ? 'italic' : '';
            const textBold = cellSettings.textBold ? 'bold' : '';
            const fontSize = cellSettings.fontSize ? cellSettings.fontSize : '10';
            const fontName = cellSettings.fontName ? cellSettings.fontName : 'Arial, sans-serif';

            font = `${textItalic} ${textBold} ${fontSize}pt ${fontName}`;
        }

        context.font = font;

        const metrics:TextMetrics = context.measureText(text);

        return metrics.width;
    };

    public static getTextHeight(text:string, boxWidth:number, fontString:string, cellSettings:any = {}):number
    {
        let div:HTMLDivElement = DomUtils._divForTextHeight;

        if( !DomUtils._divForTextHeight )
        {
            div = DomUtils._divForTextHeight = document.createElement('div');
            div.setAttribute('id', 'divForTextHeight');

            div.style.position = 'absolute';
            div.style.top = '-11111px';
            div.style.left = '-11111px';
            div.style.transform = 'translateZ(0)';
            div.style.whiteSpace = 'pre-wrap';
            div.style.wordBreak = 'break-word';

            document.body.appendChild(div);
        }

        div.textContent = text;
        div.style.width = boxWidth ? boxWidth + 'px' : '';

        let fontStyle:string;

        if( fontString )
        {
            fontStyle = fontString;
        }
        else
        {
            const textItalic = cellSettings.textItalic ? 'italic' : '';
            const textBold = cellSettings.textBold ? 'bold' : '';
            const fontSize = cellSettings.fontSize ? cellSettings.fontSize : '10';
            const fontName = cellSettings.fontName ? cellSettings.fontName : 'Arial, sans-serif';

            fontStyle = `${textItalic} ${textBold} ${fontSize}pt ${fontName}`;
        }

        div.style.font = fontStyle;
        div.style.lineHeight = '1.28581'; // body

        return div.offsetHeight;
    };

    public static getStyle(oElm:HTMLElement, strCssRule:string):string
    {
        let strValue:string = '';

        if( document.defaultView && document.defaultView.getComputedStyle )
        {
            strValue = document.defaultView.getComputedStyle(oElm, '')
                .getPropertyValue(strCssRule);
        }
        else if( (oElm as any).currentStyle )
        {
            strCssRule = strCssRule.replace(/\-(\w)/g, function(strMatch:string, p1:string)
            {
                return p1.toUpperCase();
            });

            strValue = (oElm as any).currentStyle[strCssRule];
        }

        return strValue;
    }
}
